import {
  SegmentType,
  PartitionSegmentType,
  PartitionFormat,
} from '@repo/api-gw-sdk';

import type { PathSegmentState } from '../IngestionPipelineWizardState';

/**
 * Creates a unique segment ID
 */
export function createSegmentId(): string {
  return `seg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Creates a default STRING segment with optional overrides
 */
export function createDefaultSegment(
  overrides?: Partial<PathSegmentState>
): PathSegmentState {
  return {
    id: createSegmentId(),
    segmentType: SegmentType.SEGMENT_TYPE_STRING,
    segmentStringValue: '',
    segmentPartitionType: PartitionSegmentType.PARTITION_SEGMENT_TYPE_PRIMARY,
    segmentPartitionFormat: PartitionFormat.PARTITION_FORMAT_HIVE,
    segmentPartitionName: '',
    ...overrides,
  };
}

/**
 * Parses a sample path by splitting on separator and auto-detecting segment types
 */
export function parseSamplePath(
  path: string,
  separator: string
): PathSegmentState[] {
  const parts = path.split(separator).filter((part) => part.length > 0);

  let foundPrimary = false;

  return parts.map((part) => {
    // Check if this looks like a partition (contains '=')
    const partitionMatch = /^([^=]+)=(.*)$/.exec(part);

    if (partitionMatch) {
      const [, name] = partitionMatch;
      const partitionType = !foundPrimary
        ? PartitionSegmentType.PARTITION_SEGMENT_TYPE_PRIMARY
        : PartitionSegmentType.PARTITION_SEGMENT_TYPE_SECONDARY;
      foundPrimary = true;
      return createDefaultSegment({
        segmentType: SegmentType.SEGMENT_TYPE_PARTITION,
        segmentPartitionName: name,
        segmentPartitionFormat: PartitionFormat.PARTITION_FORMAT_HIVE,
        segmentPartitionType: partitionType,
      });
    }

    // Wildcard segment
    if (part === '*') {
      return createDefaultSegment({
        segmentType: SegmentType.SEGMENT_TYPE_WILDCARD,
      });
    }

    // Otherwise, create a STRING segment
    return createDefaultSegment({
      segmentType: SegmentType.SEGMENT_TYPE_STRING,
      segmentStringValue: part,
    });
  });
}

/**
 * Returns preview text for a segment
 */
export function segmentPreviewText(seg: PathSegmentState): string {
  if (seg.segmentType === SegmentType.SEGMENT_TYPE_STRING) {
    return seg.segmentStringValue || '?';
  }

  if (seg.segmentType === SegmentType.SEGMENT_TYPE_WILDCARD) {
    return '*';
  }

  if (seg.segmentType === SegmentType.SEGMENT_TYPE_PARTITION) {
    const name = seg.segmentPartitionName || '?';
    if (seg.segmentPartitionFormat === PartitionFormat.PARTITION_FORMAT_HIVE) {
      return `${name}={${name}}`;
    }
    return `{${name}}`;
  }

  return '?';
}

/**
 * Validates segments and returns error message or null
 */
export function validateSegments(segments: PathSegmentState[]): string | null {
  if (segments.length === 0) {
    return 'At least one segment is required.';
  }

  const partitionSegments = segments.filter(
    (seg) => seg.segmentType === SegmentType.SEGMENT_TYPE_PARTITION
  );

  const primarySegments = partitionSegments.filter(
    (seg) =>
      seg.segmentPartitionType ===
      PartitionSegmentType.PARTITION_SEGMENT_TYPE_PRIMARY
  );

  if (primarySegments.length > 1) {
    return 'Only one PRIMARY partition is allowed.';
  }

  // Check for empty partition names
  if (
    partitionSegments.some(
      (seg) =>
        !seg.segmentPartitionName || seg.segmentPartitionName.trim() === ''
    )
  ) {
    return 'All partition segments must have a name.';
  }

  // Check for duplicate partition names
  const partitionNames = partitionSegments.map(
    (seg) => seg.segmentPartitionName
  );
  const uniqueNames = new Set(partitionNames);
  if (uniqueNames.size !== partitionNames.length) {
    return 'Partition names must be unique.';
  }

  // Check for empty STRING values
  const stringSegments = segments.filter(
    (seg) => seg.segmentType === SegmentType.SEGMENT_TYPE_STRING
  );
  if (
    stringSegments.some(
      (seg) => !seg.segmentStringValue || seg.segmentStringValue.trim() === ''
    )
  ) {
    return 'STRING segments must have a value.';
  }

  return null;
}

/**
 * Checks whether a primary partition exists among the given segments,
 * optionally excluding a segment by ID.
 */
export function hasPrimaryPartition(
  segments: PathSegmentState[],
  excludeId?: string
): boolean {
  return segments.some(
    (s) =>
      s.id !== excludeId &&
      s.segmentType === SegmentType.SEGMENT_TYPE_PARTITION &&
      s.segmentPartitionType ===
        PartitionSegmentType.PARTITION_SEGMENT_TYPE_PRIMARY
  );
}

/**
 * Color set for segment/partition type styling
 */
export interface ColorSet {
  bg: string;
  text: string;
  border: string;
}

/**
 * Color scheme for different segment types
 */
/* eslint-disable @eon/no-hardcoded-colors */
export const SEGMENT_COLORS: Record<string, ColorSet> = {
  [SegmentType.SEGMENT_TYPE_STRING]: {
    bg: '#eeeeee',
    text: '#616161',
    border: '#bdbdbd',
  },
  [SegmentType.SEGMENT_TYPE_PARTITION]: {
    bg: '#e3f2fd',
    text: '#1565c0',
    border: '#90caf9',
  },
  [SegmentType.SEGMENT_TYPE_WILDCARD]: {
    bg: '#fff3e0',
    text: '#e65100',
    border: '#ffb74d',
  },
};

/**
 * Per-partition-type color palette
 */
export const PARTITION_TYPE_COLORS: Record<string, ColorSet> = {
  [PartitionSegmentType.PARTITION_SEGMENT_TYPE_PRIMARY]: {
    bg: '#e3f2fd',
    text: '#1565c0',
    border: '#90caf9',
  },
  [PartitionSegmentType.PARTITION_SEGMENT_TYPE_SECONDARY]: {
    bg: '#f1f8e9',
    text: '#558b2f',
    border: '#c5e1a5',
  },
  [PartitionSegmentType.PARTITION_SEGMENT_TYPE_DATE]: {
    bg: '#f1f8e9',
    text: '#558b2f',
    border: '#c5e1a5',
  },
};

const DEFAULT_COLORS: ColorSet = {
  bg: '#eeeeee',
  text: '#616161',
  border: '#bdbdbd',
};

/**
 * Returns the color set for a segment, using partition-type-specific blues for PARTITION segments
 */
export function getSegmentColors(seg: PathSegmentState): ColorSet {
  if (seg.segmentType === SegmentType.SEGMENT_TYPE_PARTITION) {
    return (
      PARTITION_TYPE_COLORS[seg.segmentPartitionType] ??
      PARTITION_TYPE_COLORS[
        PartitionSegmentType.PARTITION_SEGMENT_TYPE_SECONDARY
      ]
    );
  }
  return SEGMENT_COLORS[seg.segmentType] ?? DEFAULT_COLORS;
}
/* eslint-enable @eon/no-hardcoded-colors */
